import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import { useBreakpoint } from "@core-ui/hooks/useBreakpoint";
import { cn } from "@core-ui/utils/cn";
import t from "@ext/localization/locale/translate";
import { Root as VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@ui-kit/Button";
import { Dialog, DialogContent, DialogTitle } from "@ui-kit/Dialog";
import { ContentDivider } from "@ui-kit/Divider";
import { useCallback, useState } from "react";
import { Logo, TopContainerWrapper } from "../../../components/HomePage/Welcome/Editor";
import FetchService from "../../../ui-logic/ApiServices/FetchService";
import { GesCloudSignInButtons } from "../../enterpriseCommon/components/signIn/GesCloudSignInButtons";

interface SignInGesCloudFormProps {
	allowContinueWithoutAccount: boolean;
	className?: string;
	gesCloudUrl?: string;
}

interface SignInGesCloudFormModalProps extends SignInGesCloudFormProps {
	onClose: () => void;
}

export const GesCloudSignInFormModal = (props: SignInGesCloudFormModalProps) => {
	const { allowContinueWithoutAccount, onClose, className, gesCloudUrl } = props;
	const [open, setOpen] = useState(true);

	const onOpenChangeHandler = useCallback(
		(open: boolean) => {
			setOpen(open);
			if (!open) onClose();
		},
		[onClose],
	);

	return (
		<Dialog onOpenChange={onOpenChangeHandler} open={open}>
			<DialogContent data-modal-root>
				<VisuallyHidden>
					<DialogTitle>{t("welcome.editor.title")}</DialogTitle>
				</VisuallyHidden>
				<div className="p-4">
					<SignInGesCloudForm
						allowContinueWithoutAccount={allowContinueWithoutAccount}
						className={className}
						gesCloudUrl={gesCloudUrl}
					/>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export const SignInGesCloudForm = ({
	allowContinueWithoutAccount,
	className,
	gesCloudUrl: gesCloudUrlOverride,
}: SignInGesCloudFormProps) => {
	const breakpoint = useBreakpoint();
	const apiUrlCreator = ApiUrlCreatorService.value;

	const handleContinueWithoutAccount = useCallback(async () => {
		await FetchService.fetch(apiUrlCreator.getDisableCloudUrl());
		refreshPage();
	}, []);

	return (
		<div className={cn("flex flex-col gap-6 form-wrap", className)}>
			<div className="header flex flex-col justify-center items-center text-center p-6 pb-0 gap-3">
				<Logo isMobile={breakpoint === "sm"} />
				<TopContainerWrapper className={cn(breakpoint === "sm" && "mobile")}>
					<h1 className="text-2xl font-semibold sm:text-lg">{t("welcome.editor.title")}</h1>
					<div className="description text-base text-muted sm:text-sm font-normal">
						{t("enterprise-cloud-guest.description")}
					</div>
				</TopContainerWrapper>
			</div>
			<GesCloudSignInButtons gesCloudUrl={gesCloudUrlOverride} />
			{allowContinueWithoutAccount && (
				<>
					<ContentDivider>
						<div className="text-sm text-center font-normal text-muted whitespace-nowrap">{t("or")}</div>
					</ContentDivider>
					<Button onClick={handleContinueWithoutAccount} type="button" variant="text">
						{t("enterprise-cloud-guest.buttons.continueWithoutAccount")}
					</Button>
				</>
			)}
		</div>
	);
};
